import {FC, useEffect, useRef, useState} from "react";
import * as d3 from "d3";
import CloseIcon from "../../../assets/icons/close.svg";
import IconEyeClose from "../../../assets/icons/filters/eye-close.svg";
import IconEyeOpen from "../../../assets/icons/filters/IconEye.svg";
import {
  IPeople,
  IPrevPeople,
  IStakeholder,
} from "../../../pages/StateInitiative/StateInitiative.type";
import {IconContainer} from "../../ClusterFilter/styles";
import {CustomSwitch} from "../../../ui/CustomSwitch";
import {urlApiDataCRGCI} from "../../../helper/api";
import {SidebarCardWorkerNew} from "../SidebarCardWorkerNew";
import {
  buildImageUrl,
  handleImageError,
  IMAGE_FALLBACK_SRC,
} from "../../../helper/imageUtils";

const ImgMockUser = IMAGE_FALLBACK_SRC;
interface IProps {
  isOpen: boolean;
  onClose: () => void;

  activeWorker?: IPeople;
  arrPersonsWorker?: IPeople[];
  setIdWorkerShowConnections?: (id: string) => void;
  prevIdWorker?: IPrevPeople[];
  setPrevIdWorker?: (PrevPeople: IPrevPeople[]) => void;
  isLoading?: boolean;
}
const dimensions = {width: window.innerWidth, height: window.innerHeight};
const widthWrapper = dimensions.width - 630;
const delta = dimensions.height / 1440;
const OUTER_DIAM = 530 * delta;
const SVG_PADDING = 250 * delta;
const SVG_WIDTH = OUTER_DIAM + (widthWrapper - OUTER_DIAM);
const SVG_DIAM = OUTER_DIAM + SVG_PADDING * 2;
const MIDDLE_DIAM = 375 * delta;
const INNER_DIAM = 170 * delta;
const PHOTO_DIAM = 124 * delta;

export const SidebarCardStackHolders: FC<IProps> = ({
  isOpen,
  onClose,
  activeWorker,
  arrPersonsWorker,
  setIdWorkerShowConnections,
  prevIdWorker,
  setPrevIdWorker,
  isLoading,
}) => {
  const [isShowAllLabels, setIsShowAllLabels] = useState(true);
  const getAngleOffsets = (count: number) => {
    if (count < 1) return [];
    if (count === 1) return [0];
    const range = 40;
    if (count % 2 === 1) {
      const mid = Math.floor(count / 2);
      const step = range / mid;
      return Array.from({length: count}, (_, i) => (i - mid) * step);
    } else {
      const step = range / (count - 1);
      return Array.from({length: count}, (_, i) => -range / 2 + i * step);
    }
  };
  const showCard = ({
    svg,
    x,
    y,
    center,
    fullInfo,
    connectionPhoto,
  }: {
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    x: number;
    y: number;
    center: number;
    fullInfo: {
      fullName: string;
      role: string;
      department: string;
    };
    connectionPhoto?: string;
  }) => {
    const department = fullInfo.department || "";
    const name = fullInfo?.fullName || "";
    const position = fullInfo?.role || "";

    const tempDept = svg
      .append("text")
      .attr("font-size", 14)
      .attr("font-family", "inherit")
      .text(department);
    const tempName = svg
      .append("text")
      .attr("font-size", 18)
      .attr("font-family", "inherit")
      .attr("font-weight", "bold")
      .text(name);
    const tempPos = svg
      .append("text")
      .attr("font-size", 14)
      .attr("font-family", "inherit")
      .text(position);

    const deptWidth = tempDept.node()?.getBBox().width || 0;
    const nameWidth = tempName.node()?.getBBox().width || 0;
    const posWidth = tempPos.node()?.getBBox().width || 0;

    tempDept.remove();
    tempName.remove();
    tempPos.remove();

    const textPadding = 40;
    const photoWidth = 60;
    const cardWidth =
      Math.max(deptWidth, nameWidth, posWidth) + textPadding + photoWidth;
    const finalCardWidth = Math.min(cardWidth, 300);

    const cardHeight = 90;

    const isRight = x > center;
    const offset = 20;
    const cardX = isRight ? x - finalCardWidth - offset : x + offset;
    const cardY = y - cardHeight / 2;

    const cardGroup = svg.append("g").attr("class", "worker-card");

    cardGroup
      .append("rect")
      .attr("x", cardX)
      .attr("y", cardY)
      .attr("width", finalCardWidth)
      .attr("height", cardHeight)
      .attr("rx", 16)
      .attr("ry", 16)
      .attr("fill", "#232323")
      .attr("stroke", "rgba(255,255,255,0.2")
      .attr("stroke-width", 2)
      .attr("filter", "drop-shadow(0px 2px 8px rgba(0,0,0,0.2))");

    const photoClipId = `card-photo-clip-${Date.now()}`;
    cardGroup
      .append("clipPath")
      .attr("id", photoClipId)
      .append("rect")
      .attr("x", cardX + 5)
      .attr("y", cardY + 5)
      .attr("width", 50)
      .attr("height", 50)
      .attr("rx", 8)
      .attr("ry", 8);

    const photoUrl =
      connectionPhoto ||
      `${urlApiDataCRGCI}${activeWorker?.photo?.formats?.thumbnail?.url || ""}`;

    const loaderId = `loader-${Date.now()}`;
    const loaderGroup = cardGroup.append("g").attr("id", loaderId);

    loaderGroup
      .append("rect")
      .attr("x", cardX + 5)
      .attr("y", cardY + 5)
      .attr("width", 50)
      .attr("height", 50)
      .attr("rx", 8)
      .attr("ry", 8)
      .attr("fill", "#1e1e1e")
      .attr("stroke", "#11A0DA")
      .attr("stroke-width", 1);

    const loaderCircle = loaderGroup
      .append("circle")
      .attr("cx", cardX + 30)
      .attr("cy", cardY + 30)
      .attr("r", 12)
      .attr("fill", "none")
      .attr("stroke", "#11A0DA")
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "round")
      .attr("stroke-dasharray", "24 24")
      .attr("opacity", 0.8);

    // Анимация вращения
    loaderCircle
      .transition()
      .duration(1000)
      .ease(d3.easeLinear)
      .attrTween("transform", function () {
        return d3.interpolateString("rotate(0)", `rotate(360)`);
      })
      .attr("transform-origin", `${cardX + 30} ${cardY + 30}`)
      .on("end", function repeat() {
        d3.select(this)
          .transition()
          .duration(1000)
          .ease(d3.easeLinear)
          .attrTween("transform", function () {
            return d3.interpolateString("rotate(0)", `rotate(360)`);
          })
          .attr("transform-origin", `${cardX + 30} ${cardY + 30}`)
          .on("end", repeat);
      });

    const cardImage = cardGroup
      .append("image")
      .attr("href", photoUrl)
      .attr("x", cardX + 5)
      .attr("y", cardY + 5)
      .attr("width", 50)
      .attr("height", 50)
      .attr("clip-path", `url(#${photoClipId})`)
      .attr("preserveAspectRatio", "xMidYMid slice")
      .style("opacity", 0);

    cardImage.on("load", function () {
      loaderGroup.remove();
      d3.select(this).transition().duration(300).style("opacity", 1);
    });

    cardImage.on("error", function () {
      loaderGroup.remove();
      d3.select(this)
        .attr("href", ImgMockUser)
        .transition()
        .duration(300)
        .style("opacity", 1);
    });

    const textStartX = cardX + photoWidth + 10;
    const departmentText = department || "";
    const tempDeptText = svg
      .append("text")
      .attr("font-size", 14)
      .attr("font-family", "inherit")
      .text(departmentText);

    const departmentTextWidth = tempDeptText.node()?.getBBox().width || 0;
    tempDeptText.remove();

    const maxTextWidth = 230;
    const textOffset = 20;
    const departmentLines =
      departmentTextWidth > maxTextWidth
        ? departmentText.split(" ").reduce((lines: string[], word) => {
            const lastLine = lines[lines.length - 1];
            const testLine = lastLine ? `${lastLine} ${word}` : word;
            const testText = svg
              .append("text")
              .attr("font-size", 14)
              .attr("font-family", "inherit")
              .text(testLine);
            const testLineWidth = testText.node()?.getBBox().width || 0;
            testText.remove();
            if (testLineWidth > maxTextWidth) {
              lines.push(word);
            } else {
              if (lines.length === 0) {
                lines.push(word);
              } else {
                lines[lines.length - 1] = testLine;
              }
            }
            return lines;
          }, [])
        : [departmentText];

    departmentLines.forEach((line, i) => {
      cardGroup
        .append("text")
        .attr("x", textStartX)
        .attr("y", cardY + textOffset + i * textOffset)
        .attr("fill", "#aaa")
        .attr("font-size", 14)
        .attr("font-family", "inherit")
        .text(line);
    });

    cardGroup
      .append("text")
      .attr("x", textStartX)
      .attr("y", cardY + textOffset + departmentLines.length * (textOffset + 2))
      .attr("fill", "#fff")
      .attr("font-size", 18)
      .attr("font-family", "inherit")
      .attr("font-weight", "bold")
      .text(name);

    cardGroup
      .append("text")
      .attr("x", textStartX)
      .attr(
        "y",
        cardY + textOffset + departmentLines.length * (textOffset + 2) + 20
      )
      .attr("fill", "#aaa")
      .attr("font-size", 14)
      .attr("font-family", "inherit")
      .text(position);
  };

  const showLabelStackholdersCircle = ({
    svg,
    x,
    y,
    center,
    fullName,
  }: {
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    x: number;
    y: number;
    center: {
      x: number;
      y: number;
    };
    fullName: string;
  }) => {
    const labelText = fullName.split(" ")[0] || fullName;
    const tempText = svg
      .append("text")
      .attr("font-size", 16)
      .attr("font-family", "inherit")
      .text(labelText);

    const textWidth = tempText.node()?.getBBox().width || 80;
    const textHeight = tempText.node()?.getBBox().height || 20;
    tempText.remove();

    const labelOffsetX =
      x > center.x
        ? -textWidth / 2 + 15
        : x === center.x
        ? textWidth / 2 + 10
        : textWidth / 2 - 15;
    const labelAnchor = x > center.x ? "start" : "end";
    const isAboveCenter = y < center.y;
    const labelOffsetY = isAboveCenter ? 25 : -25;

    const paddingX = 10;
    const paddingY = 4;
    const rectWidth = textWidth + paddingX * 2;
    const rectHeight = textHeight + paddingY * 2;
    const rectX = x + labelOffsetX - (labelAnchor === "end" ? rectWidth : 0);
    const rectY = y + labelOffsetY - rectHeight / 2;
    svg
      .append("rect")
      .attr("x", rectX)
      .attr("y", rectY)
      .attr("width", rectWidth)
      .attr("height", rectHeight)
      .attr("rx", 16)
      .attr("ry", 16)
      .attr("fill", "rgba(100, 181, 246, 0.2)");
    svg
      .append("text")
      .attr("x", rectX + rectWidth / 2)
      .attr("y", rectY + rectHeight / 2)
      .attr("fill", "#fff")
      .attr("font-size", 16)
      .attr("font-family", "inherit")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .text(labelText);
  };

  const showLabelStackholders = ({
    svg,
    x2,
    y2,
    center,
    diamondShort,
    fullName,
  }: {
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    x2: number;
    y2: number;
    center: {x: number; y: number};
    diamondShort: any;
    fullName: string;
  }) => {
    const fromCenterX = x2 - center.x;
    const fromCenterY = y2 - center.y;
    const fromCenterLen = Math.sqrt(
      fromCenterX * fromCenterX + fromCenterY * fromCenterY
    );

    const labelOffset = 24;

    // Размеры лейбла - показываем только фамилию
    const diamondLabel = fullName.split(" ")[0] || fullName;
    const tempDiamondText = svg
      .append("text")
      .attr("font-size", 16)
      .attr("font-family", "inherit")
      .text(diamondLabel);

    const diamondTextWidth = tempDiamondText.node()?.getBBox().width || 80;
    const diamondTextHeight = tempDiamondText.node()?.getBBox().height || 20;
    tempDiamondText.remove();

    const diamondPaddingX = 10;
    const diamondPaddingY = 4;
    const diamondRectWidth = diamondTextWidth + diamondPaddingX * 2;
    const diamondRectHeight = diamondTextHeight + diamondPaddingY * 2;

    const angleFromCenter = Math.atan2(fromCenterY, fromCenterX);
    const horizontalBoost =
      Math.abs(Math.cos(angleFromCenter)) * diamondRectWidth * 0.4;

    const extraX = horizontalBoost * Math.sign(fromCenterX);
    const labelCenterX =
      fromCenterX > 0
        ? x2 +
          (fromCenterX / fromCenterLen) * (diamondShort / 2 + labelOffset) +
          extraX +
          70
        : x2 +
          (fromCenterX / fromCenterLen) * (diamondShort / 2 + labelOffset) +
          extraX -
          70;

    const labelCenterY =
      y2 + (fromCenterY / fromCenterLen) * (diamondShort / 2 + labelOffset);

    const diamondRectX = labelCenterX - diamondRectWidth / 2;
    const diamondRectY = labelCenterY - diamondRectHeight / 2;

    svg
      .append("rect")
      .attr("x", diamondRectX)
      .attr("y", diamondRectY)
      .attr("width", diamondRectWidth)
      .attr("height", diamondRectHeight)
      .attr("rx", 16)
      .attr("ry", 16)
      .attr("fill", "rgba(100, 181, 246, 0.2)");

    svg
      .append("text")
      .attr("x", diamondRectX + diamondRectWidth / 2)
      .attr("y", diamondRectY + diamondRectHeight / 2)
      .attr("fill", "#fff")
      .attr("font-size", 16)
      .attr("font-family", "inherit")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .text(diamondLabel);
  };

  const svgRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    if (!isOpen || !svgRef.current || !activeWorker) {
      return;
    }

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const centerWidth = SVG_WIDTH / 2;
    const centerHeight = SVG_DIAM / 2;

    svg
      .append("circle")
      .attr("cx", centerWidth)
      .attr("cy", centerHeight)
      .attr("r", OUTER_DIAM / 2)
      .attr("fill", "none")
      .attr("stroke", "rgba(17, 160, 218, 0.3)")
      .attr("stroke-width", 1.5);

    svg
      .append("circle")
      .attr("cx", centerWidth)
      .attr("cy", centerHeight)
      .attr("r", MIDDLE_DIAM / 2)
      .attr("fill", "none")
      .attr("stroke", "rgba(17, 160, 218, 0.3)")
      .attr("stroke-width", 1.5);
    svg
      .append("circle")
      .attr("cx", centerWidth)
      .attr("cy", centerHeight)
      .attr("r", INNER_DIAM / 2)
      .attr("fill", "none")
      .attr("stroke", "#11A0DA")
      .attr("stroke-width", 2);

    svg
      .append("clipPath")
      .attr("id", "photo-clip")
      .append("circle")
      .attr("cx", centerWidth)
      .attr("cy", centerHeight)
      .attr("r", PHOTO_DIAM / 2);

    const photoUrl = buildImageUrl(
      urlApiDataCRGCI || "",
      activeWorker?.photo?.formats?.thumbnail?.url
    );

    const imageElement = svg
      .append("image")
      .attr("href", photoUrl)
      .attr("x", centerWidth - PHOTO_DIAM / 2)
      .attr("y", centerHeight - PHOTO_DIAM / 2)
      .attr("width", PHOTO_DIAM)
      .attr("height", PHOTO_DIAM)
      .attr("clip-path", "url(#photo-clip)")
      .attr("preserveAspectRatio", "xMidYMid slice");

    imageElement.on("error", function () {
      d3.select(this).attr("href", ImgMockUser);
    });

    if (!activeWorker.connections || activeWorker.connections.length === 0) {
      const messageText = isLoading ? "Загрузка связей..." : "Нет связей";

      svg
        .append("text")
        .attr("x", centerWidth)
        .attr("y", centerHeight + PHOTO_DIAM / 2 + 40)
        .attr("fill", "rgba(255, 255, 255, 0.6)")
        .attr("font-size", 16)
        .attr("font-family", "inherit")
        .attr("text-anchor", "middle")
        .text(messageText);
      return;
    }

    const getAngles = (num: number): number[] => {
      if (num < 1) return [];
      const step = 360 / num;
      return Array.from({length: num}, (_, i) => (270 + i * step) % 360);
    };

    const SMALL_DIAM = 17;
    const SMALL_RADIUS = SMALL_DIAM / 2;
    const RADIUS = (MIDDLE_DIAM + INNER_DIAM) / 4;

    const angles = getAngles(activeWorker.connections.length);

    angles.forEach((deg, index) => {
      if (angles.length === 0 || !activeWorker.connections?.[index]) return;

      const rad = (deg * Math.PI) / 180;
      const x = centerWidth + RADIUS * Math.cos(rad);
      const y = centerHeight + RADIUS * Math.sin(rad);

      const currentConnection = activeWorker.connections[index];

      const connectionData = currentConnection;

      const angleOffsets = getAngleOffsets(
        connectionData?.connections?.length || 0
      );

      angleOffsets.forEach((offset, idx) => {
        const lineAngle = rad + (offset * Math.PI) / 180;
        const lineLength = (OUTER_DIAM / 2 + MIDDLE_DIAM / 2) / 2 - RADIUS;
        const x2 = x + lineLength * Math.cos(lineAngle);
        const y2 = y + lineLength * Math.sin(lineAngle);

        const person = connectionData?.connections?.[idx];
        const interactionLevel = person?.interactionLevel?.toLowerCase() || "";
        const isWarmInteraction =
          interactionLevel.includes("тёплый") ||
          interactionLevel.includes("теплый") ||
          interactionLevel.includes("Теплый контакт");
        const lineColor = isWarmInteraction ? "#ff0000" : "#fff";
        const circleColor = isWarmInteraction ? "#ff0000" : "#11A0DA";

        svg
          .append("line")
          .attr("x1", x)
          .attr("y1", y)
          .attr("x2", x2)
          .attr("y2", y2)
          .attr("stroke", lineColor)
          .attr("stroke-width", 1);

        const diamondShort = 14;

        const fullInfo = person
          ? {
              fullName: person.fullName || "",
              department: person.department || "",
              role: person.role || "",
            }
          : {
              fullName: "",
              department: "",
              role: "",
            };

        svg
          .append("circle")
          .attr("cx", x2)
          .attr("cy", y2)
          .attr("r", 8)
          .attr("fill", circleColor)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1.5)
          .style("cursor", "pointer")
          .on("mouseenter", function () {
            const connectionPhotoUrl = connectionData?.connections?.[idx]?.photo
              ?.formats?.thumbnail?.url
              ? `${urlApiDataCRGCI}${connectionData?.connections?.[idx]?.photo?.formats?.thumbnail?.url}`
              : undefined;
            showCard({
              svg,
              x: x2,
              y: y2,
              center: centerWidth,
              fullInfo,
              connectionPhoto: connectionPhotoUrl,
            });
          })
          .on("click", function () {
            if (
              setIdWorkerShowConnections &&
              connectionData?.connections?.[idx]?.documentId
            )
              setIdWorkerShowConnections(
                connectionData.connections[idx].documentId
              );
            if (setPrevIdWorker && activeWorker)
              setPrevIdWorker([
                ...(prevIdWorker || []),
                {
                  fullName: activeWorker?.fullName || "",
                  documentId: activeWorker?.documentId || "",
                },
              ]);
          })
          .on("mouseleave", function () {
            svg.selectAll(".worker-card").remove();
          });
        const fullName = connectionData?.connections?.[idx]?.fullName || "";

        if (isShowAllLabels)
          showLabelStackholders({
            svg,
            x2,
            y2,
            center: {x: centerWidth, y: centerHeight},
            diamondShort,
            fullName,
          });
      });

      const smallCirclesCoords = angles.map((deg) => {
        const rad = (deg * Math.PI) / 180;
        return {
          x: centerWidth + RADIUS * Math.cos(rad),
          y: centerHeight + RADIUS * Math.sin(rad),
          angle: rad,
        };
      });

      smallCirclesCoords.forEach(({x, y, angle}, index) => {
        const innerX = centerWidth + (INNER_DIAM / 2) * Math.cos(angle);
        const innerY = centerHeight + (INNER_DIAM / 2) * Math.sin(angle);

        const currentConnection = activeWorker.connections?.[index];
        const firstLevelInteractionLevel =
          currentConnection?.interactionLevel?.toLowerCase() || "";
        const isFirstLevelWarmInteraction =
          firstLevelInteractionLevel.includes("тёплый") ||
          firstLevelInteractionLevel.includes("теплый") ||
          firstLevelInteractionLevel.includes("Теплый контакт");
        const firstLevelLineColor = isFirstLevelWarmInteraction
          ? "#ff0000"
          : "#fff";

        svg
          .append("line")
          .attr("x1", x)
          .attr("y1", y)
          .attr("x2", innerX)
          .attr("y2", innerY)
          .attr("stroke", firstLevelLineColor)
          .attr("stroke-width", 0.5);
      });

      const fullInfo = currentConnection || {
        fullName: "",
        department: "",
        role: "",
      };

      const fullName = currentConnection?.fullName || "";

      const firstLevelInteractionLevel =
        currentConnection?.interactionLevel?.toLowerCase() || "";
      const isFirstLevelWarmInteraction =
        firstLevelInteractionLevel.includes("тёплый") ||
        firstLevelInteractionLevel.includes("теплый") ||
        firstLevelInteractionLevel.includes("Теплый контакт");
      const firstLevelCircleColor = isFirstLevelWarmInteraction
        ? "#ff0000"
        : "#86D1F2";

      svg
        .append("circle")
        .attr("cx", x)
        .attr("cy", y)
        .attr("r", SMALL_RADIUS)
        .attr("fill", firstLevelCircleColor)
        .attr("stroke", "#fff")
        .attr("stroke-width", 1)
        .style("cursor", "pointer")
        .on("mouseenter", function () {
          const fallbackPhoto = currentConnection?.connections?.[0]?.photo;

          const connectionPhotoUrl = currentConnection?.photo?.formats
            ?.thumbnail?.url
            ? `${urlApiDataCRGCI}${currentConnection?.photo?.formats?.thumbnail?.url}`
            : fallbackPhoto?.formats?.thumbnail?.url
            ? `${urlApiDataCRGCI}${fallbackPhoto?.formats?.thumbnail?.url}`
            : undefined;

          showCard({
            svg,
            x,
            y,
            center: centerWidth,
            fullInfo,
            connectionPhoto: connectionPhotoUrl,
          });
        })

        .on("click", function () {
          if (setIdWorkerShowConnections && currentConnection?.documentId)
            setIdWorkerShowConnections(currentConnection.documentId);

          if (setPrevIdWorker && activeWorker)
            setPrevIdWorker([
              ...(prevIdWorker || []),
              {
                fullName: activeWorker?.fullName || "",
                documentId: activeWorker?.documentId || "",
              },
            ]);
        })
        .on("mouseleave", function () {
          svg.selectAll(".worker-card").remove();
        });

      if (isShowAllLabels)
        showLabelStackholdersCircle({
          svg,
          x,
          y,
          center: {x: centerWidth, y: centerHeight},
          fullName,
        });
    });
  }, [
    isOpen,
    activeWorker,
    arrPersonsWorker,
    prevIdWorker,
    widthWrapper,
    isShowAllLabels,
    isLoading,
  ]);
  return (
    <>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            left: "600px",
            bottom: 0,
            top: 0,
            background: "#0C0C0C",
            color: "#fff",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "16px",
            padding: "16px",
            zIndex: 20,

            width: `${widthWrapper}px`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{minWidth: SVG_WIDTH}}>
            <svg
              ref={svgRef}
              width={SVG_WIDTH}
              height={SVG_DIAM}
              style={{display: "block", margin: "0 auto"}}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              // marginTop: "60px",
            }}
          >
            <div
              style={{
                fontSize: "18px",
                fontWeight: "100",
                color: "rgba(255, 255, 255, 0.6)",
              }}
            >
              {activeWorker?.department}
            </div>
            <div style={{fontSize: "24px", fontWeight: "600", color: "fff"}}>
              {activeWorker?.fullName}
            </div>
            <div
              style={{
                fontSize: "18px",
                fontWeight: "100",
                color: "rgba(255, 255, 255, 0.6)",
              }}
            >
              {activeWorker?.role}
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              cursor: "pointer",
              opacity: 0.9,
            }}
          >
            {/* <img src={CloseIcon} alt="CloseIcon" /> */}

            <IconContainer>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: 10,
                  }}
                >
                  <CustomSwitch
                    checked={isShowAllLabels}
                    onChange={() => setIsShowAllLabels(!isShowAllLabels)}
                  />
                  ФИО Стейкхолдеров
                </div>
                <img
                  src={isShowAllLabels ? IconEyeOpen : IconEyeClose}
                  alt=""
                />
              </div>
            </IconContainer>
          </div>
        </div>
      )}
    </>
  );
};
